import { trpc } from "../lib/trpc.js";
import {
  Bell, BellOff, CheckCircle2, AlertTriangle, Info, XCircle,
  Loader2, Check, CheckCheck
} from "lucide-react";

const typeConfig: Record<string, { icon: typeof Info; color: string; bg: string }> = {
  info: { icon: Info, color: "text-blue-400", bg: "bg-blue-500/10" },
  success: { icon: CheckCircle2, color: "text-success", bg: "bg-success/10" },
  warning: { icon: AlertTriangle, color: "text-yellow-400", bg: "bg-yellow-500/10" },
  error: { icon: XCircle, color: "text-red-400", bg: "bg-red-500/10" },
  agent: { icon: Info, color: "text-primary", bg: "bg-primary/10" },
  pipeline: { icon: Info, color: "text-purple-400", bg: "bg-purple-500/10" },
};

export function Notifications() {
  const utils = trpc.useUtils();
  const { data: notificationsList, isLoading } = trpc.notifications.list.useQuery({ limit: 50 });
  const { data: unreadData } = trpc.notifications.unreadCount.useQuery();
  const unreadCount = unreadData?.count || 0;

  const markAsRead = trpc.notifications.markAsRead.useMutation({
    onSuccess: () => {
      utils.notifications.list.invalidate();
      utils.notifications.unreadCount.invalidate();
    },
  });

  const markAllAsRead = trpc.notifications.markAllAsRead.useMutation({
    onSuccess: () => {
      utils.notifications.list.invalidate();
      utils.notifications.unreadCount.invalidate();
    },
  });

  const formatTime = (timestamp: number) => {
    const now = Date.now();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return "Agora mesmo";
    if (minutes < 60) return `${minutes}min atrás`;
    if (hours < 24) return `${hours}h atrás`;
    if (days < 7) return `${days}d atrás`;
    return new Date(timestamp).toLocaleDateString("pt-BR");
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Notificações</h1>
          <p className="text-muted-foreground text-sm mt-1">
            {unreadCount > 0 ? `${unreadCount} não lidas` : "Todas lidas"}
          </p>
        </div>
        {unreadCount > 0 ? (
          <button
            onClick={() => markAllAsRead.mutate()}
            disabled={markAllAsRead.isPending}
            className="flex items-center gap-2 px-4 py-2 bg-secondary text-secondary-foreground rounded-lg text-sm hover:bg-secondary/80 transition-colors"
          >
            {markAllAsRead.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <CheckCheck className="w-4 h-4" />
            )}
            Marcar todas como lidas
          </button>
        ) : null}
      </div>

      {/* Notifications List */}
      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="animate-pulse bg-card border border-border rounded-xl p-4">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 bg-muted rounded-lg" />
                <div className="flex-1">
                  <div className="h-4 bg-muted rounded w-1/3 mb-2" />
                  <div className="h-3 bg-muted rounded w-2/3" />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : notificationsList && notificationsList.length > 0 ? (
        <div className="space-y-2">
          {notificationsList.map((notification) => {
            const cfg = typeConfig[notification.type] || typeConfig.info;
            const TypeIcon = cfg.icon;

            return (
              <div
                key={notification.id}
                className={`bg-card border rounded-xl p-4 transition-all cursor-pointer hover:shadow-md ${
                  notification.isRead ? "border-border opacity-70" : "border-primary/20 bg-primary/5"
                }`}
                onClick={() => {
                  if (!notification.isRead) {
                    markAsRead.mutate({ notificationId: notification.id });
                  }
                }}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${cfg.bg}`}>
                    <TypeIcon className={`w-5 h-5 ${cfg.color}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <h3 className={`text-sm font-medium ${notification.isRead ? "text-muted-foreground" : "text-foreground"}`}>
                        {notification.title}
                      </h3>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className="text-xs text-muted-foreground">
                          {formatTime(notification.createdAt)}
                        </span>
                        {!notification.isRead && (
                          <span className="w-2 h-2 bg-primary rounded-full" />
                        )}
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground mt-0.5 line-clamp-2">
                      {notification.content}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-card border border-border rounded-xl p-12 text-center">
          <BellOff className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">Nenhuma notificação</h3>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">
            Você receberá notificações quando agentes completarem tarefas,
            pipelines falharem ou o sistema precisar de atenção.
          </p>
        </div>
      )}
    </div>
  );
}
